const STORAGE_KEY = "valorant-record:riot:diagnostics";
const STAGES = new Set([
    "bind_started", "gamersky_login_ready", "gamersky_login_cancelled",
    "login_window_open", "login_callback", "entitlements_started", "entitlements_completed",
    "userinfo_started", "userinfo_completed", "session_ready", "session_saved",
    "bind_request_started", "bind_response", "view_applied", "bind_succeeded", "bind_failed",
]);
const ERROR_CODES = new Set([
    "RIOT_LOGIN_UNSUPPORTED", "RIOT_LOGIN_OBSOLETE", "RIOT_LOGIN_TIMEOUT", "RIOT_LOGIN_CANCELLED",
    "RIOT_SESSION_NOT_READY", "RIOT_SESSION_EXPIRED", "RIOT_ACCOUNT_MISMATCH",
    "RIOT_STORAGE_UNAVAILABLE", "RIOT_BIND_FAILED", "RIOT_BIND_UNCONFIRMED", "RIOT_STATS_FAILED", "RIOT_QUEUE_INVALID",
    "RIOT_CLIENT_VERSION_UNAVAILABLE", "RIOT_SHARD_NOT_FOUND", "RIOT_RATE_LIMITED",
    "RIOT_UPSTREAM_UNAVAILABLE", "RIOT_RESPONSE_INVALID", "RIOT_IDENTITY_INVALID", "RIOT_NOT_FOUND",
    "RIOT_BINDING_MISMATCH", "RIOT_SNAPSHOT_SAVE_FAILED", "REQUEST_TIMEOUT",
    "ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK", "ERR_BAD_RESPONSE", "ERR_BAD_REQUEST",
    "NETWORK_ERROR", "UNKNOWN_ERROR", "GamerSky.Constants.Error.InvalidRequest",
    "GamerSky.Constants.Error.ProgramError",
]);
const VALUE_TYPES = new Set(["object", "array", "string", "number", "boolean", "undefined", "null"]);
const ERROR_TYPES = new Set(["Error", "AxiosError", "TypeError", "RangeError", "SyntaxError", "ReferenceError"]);
const BOOLEAN_FIELDS = ["hasUrlsRequested", "hasAccessToken", "hasEntitlementsToken", "hasPuuid", "hasError", "hasRiotStats", "hasView", "retryable"];

function errorCode(value) {
    if (!value) return null;
    return ERROR_CODES.has(value) ? value : "UNKNOWN_ERROR";
}

function valueType(value) {
    if (value === null) return "null";
    return Array.isArray(value) ? "array" : typeof value;
}

function safeDetails(details = {}) {
    const safe = {};
    for (const key of BOOLEAN_FIELDS) {
        if (typeof details[key] === "boolean") safe[key] = details[key];
    }
    for (const key of ["responseType", "valueType"]) {
        if (VALUE_TYPES.has(details[key])) safe[key] = details[key];
    }
    if (ERROR_TYPES.has(details.exceptionType)) safe.exceptionType = details.exceptionType;
    if (STAGES.has(details.failedAt)) safe.failedAt = details.failedAt;
    if (Object.prototype.hasOwnProperty.call(details, "errorCode")) safe.errorCode = errorCode(details.errorCode);
    for (const key of ["httpStatus", "businessCode", "requestElapsedMs"]) {
        if (Number.isFinite(details[key]) && details[key] >= 0 && details[key] <= 10000000) safe[key] = details[key];
    }
    return safe;
}

function summarizeRiotError(error) {
    return safeDetails({
        exceptionType: error && error.name,
        errorCode: errorCode(error && (error.code || error.message)),
        httpStatus: error && error.response && error.response.status,
    });
}

function summarizeRiotResponse(response) {
    return safeDetails({
        responseType: valueType(response),
        hasError: Boolean(response && response.error),
        errorCode: errorCode(response && response.error),
        hasRiotStats: Boolean(response && response.riotStats),
        hasView: Boolean(response && response.riotStats && response.riotStats.view),
        businessCode: response && response.code,
        retryable: response && response.retryable,
    });
}

function createRiotDiagnostics(storage, options = {}) {
    const startedAt = Date.now();
    const trace = {
        traceId: options.traceId || `riot-${startedAt}-${Math.random().toString(16).slice(2, 10)}`,
        startedAt: new Date(startedAt).toISOString(),
        events: [],
    };
    const report = () => JSON.stringify(trace, null, 2);
    return {
        traceId: trace.traceId,
        report,
        record(stage, details) {
            if (!STAGES.has(stage) || (options.isCurrent && !options.isCurrent())) return;
            const entry = { stage, elapsedMs: Date.now() - startedAt, ...safeDetails(details) };
            trace.events.push(entry);
            trace.events = trace.events.slice(-40);
            // Diagnostics must never alter the result of login or binding.
            try { if (storage) storage.setItem(STORAGE_KEY, JSON.stringify(trace)); } catch (error) { /* Best effort. */ }
            try {
                const logger = options.console || console;
                logger.info("[ValorantRiotBind] " + trace.traceId + " " + JSON.stringify(entry));
            } catch (error) { /* Best effort. */ }
            try { if (options.onChange) options.onChange(report()); } catch (error) { /* Best effort. */ }
        },
    };
}

function readRiotDiagnostics(storage) {
    try {
        const trace = JSON.parse(storage.getItem(STORAGE_KEY));
        if (!trace || !/^riot-\d{13}-[a-f0-9]{1,8}$/.test(trace.traceId)
            || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(trace.startedAt)
            || !Array.isArray(trace.events)) return "";
        const events = trace.events.slice(-40).filter((entry) => entry && STAGES.has(entry.stage))
            .map((entry) => ({
                stage: entry.stage,
                elapsedMs: Number.isFinite(entry.elapsedMs) && entry.elapsedMs >= 0 ? entry.elapsedMs : 0,
                ...safeDetails(entry),
            }));
        return JSON.stringify({ traceId: trace.traceId, startedAt: trace.startedAt, events }, null, 2);
    } catch (error) {
        return "";
    }
}

module.exports = { createRiotDiagnostics, readRiotDiagnostics, summarizeRiotError, summarizeRiotResponse };
